/**
 * ColumnFilterHeader — Excel-style header cell with click-to-sort caret + a
 * filter funnel that opens a popover supporting numeric (>, <, =, between),
 * text (contains), or multi-select value filtering, depending on the column
 * `type`. Stacks with other column filters in AND fashion when the parent
 * passes processed rows through `useColumnFilters` (see hook below).
 */
import React, { useMemo, useState } from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown, Filter } from 'lucide-react';
import {
  Popover, PopoverContent, PopoverTrigger,
} from './ui/popover';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Button } from './ui/button';

export const NUMERIC_OPS = [
  { value: 'gt',      label: '>' },
  { value: 'lt',      label: '<' },
  { value: 'eq',      label: '=' },
  { value: 'between', label: 'between' },
];

const empty = { sort: null, filter: null };

export function useColumnFilters(initialState = {}) {
  const [state, setState] = useState(initialState);

  const cycleSort = (key) => {
    setState((prev) => {
      const cur = prev[key]?.sort;
      let next = 'asc';
      if (cur === 'asc') next = 'desc';
      else if (cur === 'desc') next = null;
      // Clear sort on other columns — single-column sort behaviour matches Excel.
      const cleared = Object.fromEntries(
        Object.entries(prev).map(([k, v]) => [k, { ...v, sort: null }]),
      );
      return { ...cleared, [key]: { ...(prev[key] || empty), sort: next } };
    });
  };

  const setFilter = (key, filter) => {
    setState((prev) => ({ ...prev, [key]: { ...(prev[key] || empty), filter } }));
  };

  const clearFilter = (key) => {
    setState((prev) => ({ ...prev, [key]: { ...(prev[key] || empty), filter: null } }));
  };

  const clearAll = () => setState({});

  const apply = (rows, columns) => {
    let out = Array.isArray(rows) ? [...rows] : [];

    // Apply filters (AND across columns).
    for (const col of columns) {
      const f = state[col.key]?.filter;
      if (!f) continue;
      out = out.filter((r) => {
        const v = col.accessor ? col.accessor(r) : r[col.key];
        if (col.type === 'numeric') {
          const num = Number(v);
          if (!Number.isFinite(num)) return false;
          if (f.op === 'gt')      return num >  Number(f.a);
          if (f.op === 'lt')      return num <  Number(f.a);
          if (f.op === 'eq')      return num === Number(f.a);
          if (f.op === 'between') return num >= Number(f.a) && num <= Number(f.b);
          return true;
        }
        if (col.type === 'text') {
          if (!f.contains) return true;
          return String(v ?? '').toLowerCase().includes(String(f.contains).toLowerCase());
        }
        if (col.type === 'multi') {
          if (!Array.isArray(f.values) || f.values.length === 0) return true;
          return f.values.includes(String(v));
        }
        return true;
      });
    }

    // Apply single-column sort.
    const sortEntry = Object.entries(state).find(([, v]) => v?.sort);
    if (sortEntry) {
      const [key, val] = sortEntry;
      const col = columns.find((c) => c.key === key);
      if (col) {
        out.sort((a, b) => {
          const av = col.accessor ? col.accessor(a) : a[col.key];
          const bv = col.accessor ? col.accessor(b) : b[col.key];
          const aNil = av == null || av === '' || Number.isNaN(av);
          const bNil = bv == null || bv === '' || Number.isNaN(bv);
          if (aNil && bNil) return 0;
          if (aNil) return 1;
          if (bNil) return -1;
          if (col.type === 'numeric') return val.sort === 'asc' ? Number(av) - Number(bv) : Number(bv) - Number(av);
          return val.sort === 'asc'
            ? String(av).localeCompare(String(bv), undefined, { numeric: true })
            : String(bv).localeCompare(String(av), undefined, { numeric: true });
        });
      }
    }
    return out;
  };

  const activeCount = useMemo(
    () => Object.values(state).filter((v) => v?.filter || v?.sort).length,
    [state],
  );

  return { state, cycleSort, setFilter, clearFilter, clearAll, apply, activeCount };
}

const NumericPopover = ({ filter, onApply, onClear }) => {
  const [op, setOp] = useState(filter?.op || 'gt');
  const [a, setA]   = useState(filter?.a != null ? String(filter.a) : '');
  const [b, setB]   = useState(filter?.b != null ? String(filter.b) : '');
  return (
    <div className="space-y-2">
      <Select value={op} onValueChange={setOp}>
        <SelectTrigger className="h-7 text-xs" data-testid="colfilter-op"><SelectValue /></SelectTrigger>
        <SelectContent>
          {NUMERIC_OPS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
      <div className="flex gap-1">
        <Input
          type="number" value={a} onChange={(e) => setA(e.target.value)}
          placeholder="value" className="h-7 text-xs" data-testid="colfilter-a"
        />
        {op === 'between' && (
          <Input
            type="number" value={b} onChange={(e) => setB(e.target.value)}
            placeholder="upper" className="h-7 text-xs" data-testid="colfilter-b"
          />
        )}
      </div>
      <div className="flex justify-between pt-1">
        <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={onClear} data-testid="colfilter-clear">Clear</Button>
        <Button
          size="sm" className="h-6 text-xs"
          disabled={a === '' || (op === 'between' && b === '')}
          onClick={() => onApply({ op, a: Number(a), b: op === 'between' ? Number(b) : undefined })}
          data-testid="colfilter-apply"
        >Apply</Button>
      </div>
    </div>
  );
};

const TextPopover = ({ filter, onApply, onClear }) => {
  const [contains, setContains] = useState(filter?.contains || '');
  return (
    <div className="space-y-2">
      <Input
        value={contains} onChange={(e) => setContains(e.target.value)}
        placeholder="contains…" className="h-7 text-xs" data-testid="colfilter-text"
      />
      <div className="flex justify-between pt-1">
        <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={onClear}>Clear</Button>
        <Button size="sm" className="h-6 text-xs" onClick={() => onApply({ contains })}>Apply</Button>
      </div>
    </div>
  );
};

const MultiPopover = ({ filter, options, onApply, onClear }) => {
  const [selected, setSelected] = useState(new Set(filter?.values || options.map((o) => o.value)));
  const toggle = (v) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v); else next.add(v);
    setSelected(next);
  };
  return (
    <div className="space-y-2">
      <div className="max-h-44 overflow-auto border border-slate-200 dark:border-slate-700 rounded p-1">
        {options.map((opt) => (
          <label key={opt.value} className="flex items-center gap-2 px-1.5 py-0.5 hover:bg-slate-50 dark:hover:bg-slate-800 rounded cursor-pointer text-xs">
            <Checkbox checked={selected.has(opt.value)} onCheckedChange={() => toggle(opt.value)} className="h-3 w-3" />
            <span className="truncate">{opt.label}</span>
          </label>
        ))}
      </div>
      <div className="flex justify-between pt-1">
        <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={onClear}>Clear</Button>
        <Button size="sm" className="h-6 text-xs" onClick={() => onApply({ values: Array.from(selected) })}>Apply</Button>
      </div>
    </div>
  );
};

export default function ColumnFilterHeader({ col, columnState, rows, onSort, onSetFilter, onClearFilter }) {
  const [open, setOpen] = useState(false);
  const sort = columnState?.sort || null;
  const hasFilter = !!columnState?.filter;

  const multiOptions = useMemo(() => {
    if (col.type !== 'multi') return [];
    const set = new Map();
    for (const r of rows || []) {
      const v = col.accessor ? col.accessor(r) : r[col.key];
      const s = String(v ?? '');
      if (!set.has(s)) set.set(s, { value: s, label: s });
    }
    return Array.from(set.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [col, rows]);

  return (
    <span className="inline-flex items-center gap-1">
      <button
        type="button"
        onClick={() => onSort(col.key)}
        className="flex items-center gap-1 hover:text-cyan-300"
        data-testid={`colhead-sort-${col.key}`}
        title={`Sort by ${col.label}`}
      >
        <span>{col.label}</span>
        {sort === 'asc'  && <ArrowUp className="w-3 h-3 text-cyan-400" />}
        {sort === 'desc' && <ArrowDown className="w-3 h-3 text-cyan-400" />}
        {!sort           && <ArrowUpDown className="w-3 h-3 text-slate-500" />}
      </button>
      {col.filterable !== false && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={`inline-flex items-center justify-center w-4 h-4 rounded ${hasFilter ? 'text-cyan-300 bg-cyan-500/20' : 'text-slate-500 hover:text-cyan-200'}`}
              title={`Filter ${col.label}`}
              data-testid={`colhead-filter-${col.key}`}
            >
              <Filter className="w-2.5 h-2.5" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-2" align="start" data-testid={`colhead-popover-${col.key}`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Filter — {col.label}</div>
            {col.type === 'numeric' && (
              <NumericPopover
                filter={columnState?.filter}
                onApply={(f) => { onSetFilter(col.key, f); setOpen(false); }}
                onClear={() => { onClearFilter(col.key); setOpen(false); }}
              />
            )}
            {col.type === 'text' && (
              <TextPopover
                filter={columnState?.filter}
                onApply={(f) => { onSetFilter(col.key, f); setOpen(false); }}
                onClear={() => { onClearFilter(col.key); setOpen(false); }}
              />
            )}
            {col.type === 'multi' && (
              <MultiPopover
                filter={columnState?.filter}
                options={multiOptions}
                onApply={(f) => { onSetFilter(col.key, f); setOpen(false); }}
                onClear={() => { onClearFilter(col.key); setOpen(false); }}
              />
            )}
          </PopoverContent>
        </Popover>
      )}
    </span>
  );
}
